import { useEffectWithController } from '@carvana/merch-react-hooks';
import { useContext, useEffect, useMemo, useRef } from 'react';
import SortOption from '~/constants/common/SortOption';
import DeviceContext, { useIsMobile } from '~/context/common/DeviceContext';
import StaticContext from '~/context/common/StaticContext';
import UserContext from '~/context/common/UserContext';
import AvailabilityContext from '~/context/home/AvailabilityContext';
import RecentVehiclesContext from '~/context/home/RecentVehiclesContext';
import fetchInventory from '~/services/common/fetchInventory';
import { BrowserCookies } from '~/utilities';
import generateErrorLogger from '~/utilities/errorHandlers';

export default function useConvertRecentToSearch() {
  const {
    recentVehiclesFromBff: { data: bffVehicles },
    recentVehiclesFromSearch: { setLoading, setError, setData }
  } = useContext(RecentVehiclesContext);
  const {
    recentVehicleAvailability: { setData: setAvailabilityData }
  } = useContext(AvailabilityContext);

  const bffIds = useMemo(() => {
    return bffVehicles.map(({ id }) => id);
  }, [bffVehicles]);

  const {
    location: {
      data: { zip }
    }
  } = useContext(UserContext);
  const { clientId } = useContext(StaticContext);
  const { browserName } = useContext(DeviceContext);
  const isMobile = useIsMobile();
  const isMobileRef = useRef(isMobile);

  useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);

  useEffectWithController(
    controller => {
      setLoading();

      if (bffIds.length === 0) {
        setData([]);

        return;
      }

      fetchInventory(
        {
          analyticsData: {
            browser: browserName ?? '',
            clientId,
            deviceName: '',
            isMobileDevice: isMobileRef.current,
            referrer: document.referrer
          },
          browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
          filters: { vehicleIds: bffIds },
          pagination: { page: 1, pageSize: 12 },
          requestedFeatures: ['ExcludeFacetData'],
          sortBy: SortOption.MostPopular,
          zip5: zip
        },
        controller
      )
        .then(({ data: inventoryData }) => {
          const unorderedVehicles = inventoryData.inventory.vehicles;
          const orderedVehicles = unorderedVehicles.sort((a, b) => {
            return bffIds.indexOf(a.vehicleId) - bffIds.indexOf(b.vehicleId);
          });
          setData(orderedVehicles);
          setAvailabilityData(
            orderedVehicles.map(
              ({ vehicleId, vehicleInventoryType, locationId }) => ({
                vehicleId,
                vehicleLocationId: locationId,
                vehicleInventoryType
              })
            )
          );
        })
        .catch(
          generateErrorLogger('fetchSearchWithIds', {}, { callback: setError })
        );
    },
    [
      bffIds,
      browserName,
      clientId,
      setAvailabilityData,
      setData,
      setError,
      setLoading,
      zip
    ]
  );
}
